import React, {
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    Flex,
    FlexProps,
    Grid,
    Text,
} from '@chakra-ui/react';
import { BlockHeader, ImageModel } from 'shared';
import { useSearchParams } from 'react-router-dom';
import { CateringEventCategoryModel, CateringEventFormatModel, CateringFormatModel } from 'entities';
// eslint-disable-next-line no-restricted-imports
import { Tabs, TabsItemProps } from 'shared/ui/Tabs';
import { LeadSmallSection } from 'features';
// eslint-disable-next-line no-restricted-imports
import { richTextToString } from 'shared/lib/richtext-to-string';
import { Helmet } from 'react-helmet-async';
import { CateringPageMainTabsCateringEventFormatsContent } from './CateringPageMainTabsCateringEventFormatsContent';
import { CateringPageMainTabsCateringFormatsContent } from './CateringPageMainTabsCateringFormatsContent';

const createCateringPageMainTabsItemLabel = (
    label: string,
) => (
    <Text fontSize="1.5rem" transition="0.5s">{label.toLocaleUpperCase()}</Text>
);

const createCateringPageMainTabsCateringFormatsContentItem = (
    key: string,
    cateringFormats: CateringFormatModel[],
) => (
    <CateringPageMainTabsCateringFormatsContent
        cateringFormats={cateringFormats}
        transition="0.5s"
    />
);

const createCateringPageMainTabsCateringEventFormatsContentItem = (
    slug: string,
    cateringEventFormats: CateringEventFormatModel[],
) => (
    <CateringPageMainTabsCateringEventFormatsContent
        cateringEventFormats={cateringEventFormats}
        transition="0.5s"
    />
);

const createCateringPageMainTabsItems = (
    cateringEventCategories: CateringEventCategoryModel[],
    cateringFormats: CateringFormatModel[],
    cateringEventFormats: CateringEventFormatModel[],
): TabsItemProps[] => [{
    key: 'formats',
    labelComponent: createCateringPageMainTabsItemLabel('Форматы'),
    contentComponent: createCateringPageMainTabsCateringFormatsContentItem(
        'formats',
        cateringFormats,
    ),
}].concat(cateringEventCategories.map(({
    slug,
    name,
}) => ({
    key: slug,
    labelComponent: createCateringPageMainTabsItemLabel(name),
    contentComponent: createCateringPageMainTabsCateringEventFormatsContentItem(
        slug,
        cateringEventFormats,
    ),
})));

export type CateringPageMainProps = {
    cateringEventCategories: CateringEventCategoryModel[],
    cateringFormats: CateringFormatModel[],
    cateringEventFormats: CateringEventFormatModel[],
    title: string,
    description?: any,
    image: ImageModel,
    leadFormPdf?: ImageModel,
} & FlexProps & React.RefAttributes<HTMLDivElement>

export const CateringPageMain: React.FC<CateringPageMainProps> = ({
    cateringEventCategories,
    cateringFormats,
    cateringEventFormats,
    title,
    description,
    image,
    leadFormPdf,
    ...props
}) => {
    const [searchParams, setSearchParams] = useSearchParams();

    const [selectedTab, setSelectedTab] = useState(searchParams.get('tab') || 'formats');

    const ref = useRef<HTMLDivElement>(null);
    const headerReaf = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (searchParams.get('tab') !== selectedTab) {
            setSelectedTab(searchParams.get('tab') || 'formats');
        }
    }, [searchParams.get('tab')]);

    useEffect(() => {
        if (selectedTab) {
            setSearchParams({
                tab: selectedTab,
            });
        }
    }, [selectedTab]);

    const memoTabsItems = useMemo(() => (
        createCateringPageMainTabsItems(
            cateringEventCategories,
            cateringFormats,
            cateringEventFormats.filter(({ category }) => category?.slug === selectedTab),
        )
    ), [
        cateringEventCategories,
        cateringFormats,
        cateringEventFormats,
        selectedTab,
    ]);

    return (
        <Flex
            {...props}
            flexDirection="column"
            ref={ref}
        >
            <Helmet>
                <title>{title}</title>
                <meta name="description" content={richTextToString(description)} />
                <meta property="og:title" content={title} />
            </Helmet>
            <Flex
                ref={headerReaf}
            >
                <BlockHeader
                    title={title}
                    imageSrc={image.url}
                    description={description}
                    width={{
                        base: '100vw',
                        sm: '100%',
                    }}
                    background="white"
                />
            </Flex>
            <Grid
                background="#000000"
            >
                <LeadSmallSection
                    title="НЕ МОЖЕТЕ ОПРЕДЕЛИТЬСЯ?"
                    padding="2rem"
                    buttonText="Заказать звонок"
                    background="#000000"
                    maxWidth={{
                        base: '425px',
                        lg: '1280px',
                    }}
                    width="100%"
                    margin="0 auto"
                    pdfUrl={leadFormPdf?.url}
                />
            </Grid>
            <Tabs
                maxWidth={{
                    base: '425px',
                    lg: '1280px',
                }}
                background="white"
                width="100%"
                margin="0 auto"
                padding="2rem"
                value={selectedTab}
                onValueChange={(v) => setSelectedTab(v)}
                items={memoTabsItems}
            />
        </Flex>
    );
};
